import type { AccentId, SpeciesId } from '../types.js';

export interface CharacterSpec {
  id: SpeciesId;
  /** Display name used on bubbles and HUD. */
  name: string;
  accent: AccentId;
  palette: { body: string; shade: string; accent: string; eye: string };
  /** Synth voice identity (client audio). */
  voice: { pitch: number; formant: number; rasp: number };
  body: 'stalk' | 'blob' | 'round' | 'winged' | 'beast' | 'machine' | 'object' | 'tree';
  stats: { maxHp: number; armor: number; speed: number; jump: number };
  quirks: string[];
}

export const CHARACTERS: Record<SpeciesId, CharacterSpec> = {
  bunny: {
    id: 'bunny',
    name: 'Hopkin',
    accent: 'en',
    palette: { body: '#f2ead9', shade: '#d6c9b2', accent: '#f6b8c4', eye: '#2a1f18' },
    voice: { pitch: 1.55, formant: 1.3, rasp: 0.05 },
    body: 'round',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['ears', 'chipper'],
  },
  duckling: {
    id: 'duckling',
    name: 'Quacky',
    accent: 'en',
    palette: { body: '#f7d84a', shade: '#d9b62c', accent: '#f0a53b', eye: '#22211a' },
    voice: { pitch: 1.45, formant: 1.3, rasp: 0.25 },
    body: 'round',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['bill', 'chipper'],
  },
  mole: {
    id: 'mole',
    name: 'Krot',
    accent: 'ru',
    palette: { body: '#8d7f86', shade: '#6b5f66', accent: '#e8c3c9', eye: '#191419' },
    voice: { pitch: 0.8, formant: 0.7, rasp: 0.5 },
    body: 'beast',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['tough', 'grumpy'],
  },
  bat: {
    id: 'bat',
    name: 'Nochka',
    accent: 'ru',
    palette: { body: '#6b5a8a', shade: '#4c3f68', accent: '#3a3152', eye: '#ffd23f' },
    voice: { pitch: 1.25, formant: 1.1, rasp: 0.3 },
    body: 'winged',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['big-eyes'],
  },
  potato: {
    id: 'potato',
    name: 'Spud',
    accent: 'en',
    palette: { body: '#d9b382', shade: '#b58f5e', accent: '#a5764a', eye: '#241a12' },
    voice: { pitch: 0.85, formant: 0.8, rasp: 0.4 },
    body: 'blob',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['warts', 'stoic'],
  },
  radish: {
    id: 'radish',
    name: 'Rabanita',
    accent: 'es',
    palette: { body: '#f2e9ef', shade: '#d3c4cd', accent: '#e0455f', eye: '#231b20' },
    voice: { pitch: 1.4, formant: 1.25, rasp: 0.1 },
    body: 'blob',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['leafy', 'chipper'],
  },
  mushroom: {
    id: 'mushroom',
    name: 'Grib',
    accent: 'ru',
    palette: { body: '#e8e2d5', shade: '#c4bbaa', accent: '#d94f4f', eye: '#22242a' },
    voice: { pitch: 0.75, formant: 0.8, rasp: 0.4 },
    body: 'blob',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['cap', 'stoic'],
  },
  chili: {
    id: 'chili',
    name: 'Picante',
    accent: 'es',
    palette: { body: '#e23b3b', shade: '#b02727', accent: '#3f9e54', eye: '#20140f' },
    voice: { pitch: 1.45, formant: 1.3, rasp: 0.2 },
    body: 'stalk',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['hot-tempered'],
  },
  squirrel: {
    id: 'squirrel',
    name: 'Nutkin',
    accent: 'en',
    palette: { body: '#b5713c', shade: '#8a5228', accent: '#f0dfc0', eye: '#1e1712' },
    voice: { pitch: 1.5, formant: 1.25, rasp: 0.15 },
    body: 'round',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['fluffy-tail', 'chatty'],
  },
  owl: {
    id: 'owl',
    name: 'Filin',
    accent: 'ru',
    palette: { body: '#8d6e4f', shade: '#6a5039', accent: '#f4efe2', eye: '#ffd23f' },
    voice: { pitch: 0.88, formant: 0.75, rasp: 0.25 },
    body: 'winged',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['big-eyes', 'wise'],
  },
  thistle: {
    id: 'thistle',
    name: 'Needler',
    accent: 'en',
    palette: { body: '#9b6fc0', shade: '#7a53a0', accent: '#3f9e54', eye: '#241a2e' },
    voice: { pitch: 1.1, formant: 1.0, rasp: 0.45 },
    body: 'stalk',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['spines', 'sassy'],
  },
  fern: {
    id: 'fern',
    name: 'Helecho',
    accent: 'es',
    palette: { body: '#4aa88f', shade: '#337d6a', accent: '#d9f0e6', eye: '#12241f' },
    voice: { pitch: 1.2, formant: 1.1, rasp: 0.15 },
    body: 'stalk',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['fronds', 'calm'],
  },
  acorn: {
    id: 'acorn',
    name: 'Zhelud',
    accent: 'ru',
    palette: { body: '#c98f4b', shade: '#9c6c35', accent: '#6b4a2f', eye: '#221a12' },
    voice: { pitch: 1.0, formant: 0.95, rasp: 0.3 },
    body: 'blob',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['hat', 'tough'],
  },
  tree: {
    id: 'tree',
    name: 'Twiggy',
    accent: 'en',
    palette: { body: '#8a5a35', shade: '#6b4226', accent: '#3fa34d', eye: '#141a12' },
    voice: { pitch: 0.7, formant: 0.65, rasp: 0.35 },
    body: 'tree',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['leafy', 'calm'],
  },
  tractor: {
    id: 'tractor',
    name: 'Rusty',
    accent: 'en',
    palette: { body: '#d94f3f', shade: '#a6352b', accent: '#2f3336', eye: '#ffe9a8' },
    voice: { pitch: 0.62, formant: 0.55, rasp: 0.6 },
    body: 'machine',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['engine', 'grumpy'],
  },
  dinosaur: {
    id: 'dinosaur',
    name: 'Rexito',
    accent: 'es',
    palette: { body: '#5fae52', shade: '#3f7d38', accent: '#e8d44a', eye: '#1c1c22' },
    voice: { pitch: 0.78, formant: 0.8, rasp: 0.4 },
    body: 'beast',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['plates', 'grumpy'],
  },
  buffalo: {
    id: 'buffalo',
    name: 'Bizon',
    accent: 'ru',
    palette: { body: '#7a5a3c', shade: '#5c4128', accent: '#e8dcc4', eye: '#171208' },
    voice: { pitch: 0.68, formant: 0.6, rasp: 0.5 },
    body: 'beast',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['horns', 'tough'],
  },
  rock: {
    id: 'rock',
    name: 'Pebble',
    accent: 'en',
    palette: { body: '#9aa0a6', shade: '#71777d', accent: '#b9c0c6', eye: '#1c1c22' },
    voice: { pitch: 0.72, formant: 0.7, rasp: 0.55 },
    body: 'object',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['grumpy', 'tough'],
  },
  flowerpot: {
    id: 'flowerpot',
    name: 'Maceta',
    accent: 'es',
    palette: { body: '#b35a37', shade: '#8a4128', accent: '#e85d75', eye: '#1c1410' },
    voice: { pitch: 1.3, formant: 1.2, rasp: 0.1 },
    body: 'object',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['petals', 'chipper'],
  },
  corn: {
    id: 'corn',
    name: 'Elote',
    accent: 'es',
    palette: { body: '#e8c84a', shade: '#c9a52e', accent: '#3fa34d', eye: '#201a0c' },
    voice: { pitch: 1.35, formant: 1.25, rasp: 0.15 },
    body: 'stalk',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['kernels', 'chipper'],
  },
  pumpkin: {
    id: 'pumpkin',
    name: 'Patch',
    accent: 'en',
    palette: { body: '#e8862a', shade: '#c96a15', accent: '#3f7d38', eye: '#1c1410' },
    voice: { pitch: 0.9, formant: 0.85, rasp: 0.3 },
    body: 'round',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['stem', 'tough'],
  },
  turtle: {
    id: 'turtle',
    name: 'Shelly',
    accent: 'en',
    palette: { body: '#5aa06a', shade: '#3f7d50', accent: '#8a5a35', eye: '#121a14' },
    voice: { pitch: 0.95, formant: 0.9, rasp: 0.2 },
    body: 'beast',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['shell', 'calm'],
  },
  ladybug: {
    id: 'ladybug',
    name: 'Mariya',
    accent: 'ru',
    palette: { body: '#e0455f', shade: '#b12f45', accent: '#241a16', eye: '#141014' },
    voice: { pitch: 1.5, formant: 1.35, rasp: 0.1 },
    body: 'round',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['spots', 'chipper'],
  },
  snail: {
    id: 'snail',
    name: 'Sluggo',
    accent: 'en',
    palette: { body: '#d9b98a', shade: '#b5946a', accent: '#8a5a35', eye: '#17120c' },
    voice: { pitch: 1.05, formant: 1.0, rasp: 0.35 },
    body: 'blob',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['shell', 'calm'],
  },
  hedgehog: {
    id: 'hedgehog',
    name: 'Yozhik',
    accent: 'ru',
    palette: { body: '#a5764a', shade: '#7d5734', accent: '#5c4128', eye: '#17120c' },
    voice: { pitch: 1.2, formant: 1.1, rasp: 0.45 },
    body: 'round',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['spines', 'grumpy'],
  },
  bee: {
    id: 'bee',
    name: 'Buzzy',
    accent: 'en',
    palette: { body: '#f2c53d', shade: '#d1a123', accent: '#2b2b30', eye: '#14141a' },
    voice: { pitch: 1.6, formant: 1.4, rasp: 0.2 },
    body: 'winged',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['stripes', 'chipper'],
  },
  turnip: {
    id: 'turnip',
    name: 'Repka',
    accent: 'ru',
    palette: { body: '#efe8f5', shade: '#cfc4dd', accent: '#9b6fc0', eye: '#201a26' },
    voice: { pitch: 1.25, formant: 1.15, rasp: 0.2 },
    body: 'blob',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['chipper', 'leafy'],
  },
  fox: {
    id: 'fox',
    name: 'Zorro',
    accent: 'es',
    palette: { body: '#e08a3c', shade: '#b56a28', accent: '#f5e6d0', eye: '#2a1c12' },
    voice: { pitch: 1.45, formant: 1.3, rasp: 0.15 },
    body: 'round',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['ears', 'chipper'],
  },
  strawberry: {
    id: 'strawberry',
    name: 'Fresa',
    accent: 'es',
    palette: { body: '#e8556d', shade: '#c23a50', accent: '#47a05c', eye: '#231b20' },
    voice: { pitch: 1.5, formant: 1.35, rasp: 0.1 },
    body: 'round',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['stem', 'chipper'],
  },
  pineapple: {
    id: 'pineapple',
    name: 'Piña',
    accent: 'es',
    palette: { body: '#ecc94b', shade: '#cba42f', accent: '#47a05c', eye: '#241a10' },
    voice: { pitch: 1.1, formant: 1.0, rasp: 0.3 },
    body: 'stalk',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['kernels', 'spines', 'tough'],
  },
  raccoon: {
    id: 'raccoon',
    name: 'Yenot',
    accent: 'ru',
    palette: { body: '#8d8078', shade: '#6a5f59', accent: '#d9cbb8', eye: '#1c1712' },
    voice: { pitch: 1.3, formant: 1.2, rasp: 0.35 },
    body: 'round',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['ears', 'grumpy'],
  },
  penguin: {
    id: 'penguin',
    name: 'Pingvin',
    accent: 'ru',
    palette: { body: '#3b4756', shade: '#2a3240', accent: '#f0a53b', eye: '#14181e' },
    voice: { pitch: 1.0, formant: 0.95, rasp: 0.2 },
    body: 'round',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['bill', 'stoic'],
  },
  bear: {
    id: 'bear',
    name: 'Mishka',
    accent: 'ru',
    palette: { body: '#7a5433', shade: '#5c3d24', accent: '#c9a06a', eye: '#1a120a' },
    voice: { pitch: 0.7, formant: 0.62, rasp: 0.55 },
    body: 'beast',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['grumpy', 'tough'],
  },
  walrus: {
    id: 'walrus',
    name: 'Wally',
    accent: 'en',
    palette: { body: '#8a8f96', shade: '#666b72', accent: '#efe6d4', eye: '#171a1e' },
    voice: { pitch: 0.6, formant: 0.55, rasp: 0.6 },
    body: 'beast',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['horns', 'tough'],
  },
  ram: {
    id: 'ram',
    name: 'Baran',
    accent: 'ru',
    palette: { body: '#efe9dd', shade: '#cfc6b4', accent: '#d9b98a', eye: '#1e1810' },
    voice: { pitch: 0.85, formant: 0.8, rasp: 0.4 },
    body: 'beast',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['horns', 'stoic'],
  },
  dragonfly: {
    id: 'dragonfly',
    name: 'Dart',
    accent: 'en',
    palette: { body: '#4aa8b5', shade: '#337d8a', accent: '#bfeef2', eye: '#10202a' },
    voice: { pitch: 1.6, formant: 1.45, rasp: 0.1 },
    body: 'winged',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['chipper'],
  },
  tomato: {
    id: 'tomato',
    name: 'Jitomate',
    accent: 'es',
    palette: { body: '#e04a35', shade: '#b13525', accent: '#3f9e54', eye: '#20140f' },
    voice: { pitch: 1.4, formant: 1.3, rasp: 0.12 },
    body: 'round',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['stem', 'chipper'],
  },
  sheep: {
    id: 'sheep',
    name: 'Lanita',
    accent: 'es',
    palette: { body: '#f2ede2', shade: '#d6cdba', accent: '#e9b7c0', eye: '#2a1f18' },
    voice: { pitch: 1.5, formant: 1.3, rasp: 0.15 },
    body: 'beast',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['wool', 'chipper'],
  },
  cow: {
    id: 'cow',
    name: 'Daisy',
    accent: 'en',
    palette: { body: '#f5f1e8', shade: '#d8d2c4', accent: '#3a3742', eye: '#241f1a' },
    voice: { pitch: 0.7, formant: 0.7, rasp: 0.3 },
    body: 'beast',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['spots', 'horns', 'muzzle', 'tough'],
  },
  crocodile: {
    id: 'crocodile',
    name: 'Gena',
    accent: 'ru',
    palette: { body: '#5fae52', shade: '#3f8a3d', accent: '#cfe8a8', eye: '#1e2c1a' },
    voice: { pitch: 0.62, formant: 0.65, rasp: 0.55 },
    body: 'beast',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['ridges', 'long-snout', 'tough'],
  },
  elephant: {
    id: 'elephant',
    name: 'Peanut',
    accent: 'en',
    palette: { body: '#a8adb8', shade: '#848a97', accent: '#d9bcc6', eye: '#2a2630' },
    voice: { pitch: 0.6, formant: 0.55, rasp: 0.25 },
    body: 'beast',
    stats: { maxHp: 100, armor: 0, speed: 95, jump: 460 },
    quirks: ['trunk', 'big-ears', 'tough'],
  },
};

/**
 * One-line narration per character — the flavor the squad hover shows.
 * Characters are pure skins (uniform stats); this is what tells them apart.
 */
export const CHARACTER_BLURBS: Record<SpeciesId, string> = {
  bunny: 'Two ears, zero caution — hops first, thinks later.',
  duckling: 'Small, yellow, and loud about it.',
  mole: 'Sees nothing, fears nothing — digs straight through.',
  bat: 'Naps upside down; wakes up with opinions.',
  potato: 'Lumps, not bumps. Slow to anger, slower to move.',
  radish: 'Peppery little root with a fiery top.',
  mushroom: 'Damp, stoic, and quietly everywhere.',
  chili: 'Sweet smile, scorched-earth temper.',
  squirrel: 'Buries everything, remembers nothing.',
  owl: 'Wise face, judgmental stare.',
  thistle: 'Soft petals were a marketing choice.',
  fern: 'Unfurls in the shade, thrives on neglect.',
  acorn: 'Small now. Oak later. Patience.',
  tree: 'Branch manager of the whole forest.',
  tractor: 'Rusted, trusted, hard to stop.',
  dinosaur: 'Extinct once. Angry twice.',
  buffalo: 'A wall with horns and a grudge.',
  rock: 'Takes hits for breakfast, chips for dessert.',
  flowerpot: 'Flower power in a ceramic shell.',
  corn: 'Buttered, battle-ready, beloved.',
  pumpkin: 'Round, ribbed, and ready to roll.',
  turtle: 'Home is where the shell is — slow but sure.',
  ladybug: 'Seven spots, zero mercy.',
  snail: 'Carries home, leaves slime, takes its time.',
  hedgehog: 'Hugs are strongly inadvisable.',
  bee: 'Small wings, big attitude, sharper tongue.',
  turnip: 'Pulls her weight — and everyone else’s.',
  fox: 'Charming smile, questionable aim.',
  strawberry: 'Sweet outside, seeds of chaos within.',
  pineapple: 'Sweetheart with a spiky crown.',
  raccoon: 'Bandit mask, sticky fingers.',
  penguin: 'Dressed for the occasion. Every occasion.',
  bear: 'Big paws, bigger appetite.',
  walrus: 'Tusks out, naps in.',
  ram: 'Headfirst through every problem.',
  dragonfly: 'A needle with wings and no brakes.',
  tomato: 'Ripe, red, and ready to splat.',
  sheep: 'A walking cotton ball with opinions.',
  cow: 'Pasture princess with a spotted temper.',
  crocodile: 'Smiles wide. Bites wider.',
  elephant: 'Never forgets a grudge. Or a snack.',
};

export const SPECIES_ORDER: SpeciesId[] = [
  'bunny',
  'duckling',
  'mushroom',
  'mole',
  'squirrel',
  'ladybug',
  'snail',
  'hedgehog',
  'bee',
  'bat',
  'potato',
  'radish',
  'turnip',
  'acorn',
  'chili',
  'thistle',
  'fern',
  'pumpkin',
  'turtle',
  'flowerpot',
  'corn',
  'tree',
  'tractor',
  'dinosaur',
  'buffalo',
  'rock',
  'owl',
  'fox',
  'strawberry',
  'pineapple',
  'raccoon',
  'penguin',
  'bear',
  'walrus',
  'ram',
  'dragonfly',
  'tomato',
  'sheep',
  'cow',
  'crocodile',
  'elephant',
];

/** Team banner colors (index by team id). */
export const TEAM_COLORS = ['#ff5a5a', '#4a9dff', '#ffb84a', '#c06bff'];
